import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Archive, ArrowLeftRight, Boxes, Check, ChevronRight, CirclePlus,
  Eye, EyeOff, Image as ImageIcon, LayoutDashboard, LoaderCircle,
  Package, Pencil, Plus, RefreshCw, Search, Settings2, ShoppingBag, Trash2,
  Upload, X,
} from "./virtualShopIcons.jsx";
import { MUTATION, READ, ownerMediaContentUrl, responseValue } from "./virtualShopAdminApi.js";
import {
  ASSET_KINDS, MAX_MEDIA_BYTES, MEDIA_TYPES, SHOP_STATUSES, blankProduct, nextPageNumber,
  normaliseSlots, pageFilledCount, productPayload, validateProduct,
} from "./virtualShopModel.js";
import VirtualShopStorefrontContent from "./VirtualShopStorefrontContent.jsx";
import VirtualShopExperienceAdmin from "./VirtualShopExperienceAdmin.jsx";
import VirtualShopModeration from "./VirtualShopModeration.jsx";
import VirtualShopConfigurations from "./VirtualShopConfigurations.jsx";
import { VirtualShopImporter, VirtualShopOwnershipSupport } from "./VirtualShopOperations.jsx";
import "./virtualShopOwner.css";

const NAV = Object.freeze([
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "sections", label: "Shop Sections", icon: Boxes },
  { id: "products", label: "Products", icon: Package },
  { id: "configurations", label: "Product Configurations", icon: Settings2 },
  { id: "media", label: "Media / Images", icon: ImageIcon },
  { id: "storefront", label: "Storefront Content", icon: Settings2 },
  { id: "experience", label: "World Experience", icon: Eye },
  { id: "moderation", label: "Moderation", icon: EyeOff },
  { id: "import", label: "Import Store Catalogue", icon: Upload },
  { id: "ownership", label: "Ownership Support", icon: Search },
]);

const STATUS_LABEL = Object.freeze({ draft: "Draft", published: "Published", hidden: "Hidden", archived: "Archived" });
const LIST_PAGE_SIZE = 50;
const PICKER_PAGE_SIZE = 24;
const PROGRESSION_LEVELS = Object.freeze([
  { value: 1, label: "1 · Everyday" },
  { value: 2, label: "2 · Upgraded" },
  { value: 3, label: "3 · Premium" },
  { value: 4, label: "4 · Elite" },
  { value: 5, label: "5 · Unreal" },
]);
const ASSET_KIND_LABELS = Object.freeze({
  standard: "Standard", vehicle: "Vehicle", property: "Property", component: "Component",
  furnishing: "Furnishing", apparel: "Apparel", collectible: "Collectible", equipment: "Equipment",
});

function requestKey() {
  return window.crypto?.randomUUID?.() || `vshop-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function errorMessage(result, fallback = "The request could not be completed.") {
  if (typeof result?.message === "string" && result.message.trim()) return result.message;
  const code = result?.reason;
  const messages = {
    forbidden: "Owner access is required for this action.",
    expired: "Your VISINVOKE Admin session has expired. Sign in again.",
    conflict: "The record changed or that position is no longer available. Refresh and try again.",
    "payload-too-large": "That request is too large.",
    "invalid-media": "Choose a valid JPEG, PNG, or WebP image.",
    "upload-failed": "The image could not be uploaded.",
  };
  return messages[code] || fallback;
}

function Busy({ label = "Loading" }) {
  return <div className="vs-busy"><LoaderCircle size={22} aria-hidden="true" /><span>{label}</span></div>;
}

function Empty({ icon: Icon = Package, title, text, action }) {
  return (
    <div className="vs-empty">
      <span className="vs-empty-icon"><Icon size={28} aria-hidden="true" /></span>
      <h3>{title}</h3>
      <p>{text}</p>
      {action}
    </div>
  );
}

function Status({ value }) {
  const status = SHOP_STATUSES.includes(value) ? value : "draft";
  return <span className={`vs-status is-${status}`}>{STATUS_LABEL[status]}</span>;
}

function ChoiceButtons({ label, items, value, onChange, className = "", autoFocus = false }) {
  return (
    <fieldset className={`vs-choice-buttons ${className}`.trim()}>
      <legend>{label}</legend>
      <div>{items.map((item, index) => <button type="button" key={String(item.value)} autoFocus={autoFocus && index === 0}
        className={value === item.value ? "is-active" : ""} aria-pressed={value === item.value}
        onClick={() => onChange(item.value)}>{item.label}</button>)}</div>
    </fieldset>
  );
}

function Notice({ notice, onClear }) {
  if (!notice) return null;
  return (
    <div className={`vs-notice is-${notice.kind || "info"}`} role={notice.kind === "error" ? "alert" : "status"}>
      <span>{notice.message}</span>
      <button type="button" aria-label="Dismiss message" onClick={onClear}><X size={16} /></button>
    </div>
  );
}

function Modal({ title, subtitle, onClose, children, wide = false }) {
  useEffect(() => {
    const close = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  return (
    <div className="vs-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className={`vs-modal ${wide ? "is-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <div><h2>{title}</h2>{subtitle ? <p>{subtitle}</p> : null}</div>
          <button className="vs-icon-button" type="button" onClick={onClose} aria-label="Close"><X size={19} /></button>
        </header>
        <div className="vs-modal-body">{children}</div>
      </section>
    </div>
  );
}

function MediaThumb({ media, alt = "" }) {
  const source = ownerMediaContentUrl(media);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [source]);
  return source && !failed
    ? <img src={source} alt={media.altText || alt} onError={() => setFailed(true)} />
    : <span className="vs-image-placeholder"><ImageIcon size={25} aria-hidden="true" /></span>;
}
function Pager({ page, total, pageSize, onPage, label = "items" }) {
  const pageCount = Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
  if (pageCount <= 1) return null;
  return (
    <nav className="vs-pager" aria-label={`${label} pages`}>
      <button className="vs-secondary" type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span>Page {page} of {pageCount} <small>· {total} {label}</small></span>
      <button className="vs-secondary" type="button" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>Next</button>
    </nav>
  );
}

function MediaPicker({ media, onFindMedia, selectedId, onSelect, onClose, title = "Choose an image" }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(media.length);
  const [items, setItems] = useState(media.filter((item) => item.status !== "archived").slice(0, PICKER_PAGE_SIZE));
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      const result = await onFindMedia({ search, page, pageSize: PICKER_PAGE_SIZE, status: "active" });
      if (active) {
        setItems(result.items);
        setTotal(result.total);
        setLoading(false);
      }
    }, 180);
    return () => { active = false; clearTimeout(timer); };
  }, [search, page, onFindMedia]);
  return (
    <Modal title={title} subtitle="Select an owner-supplied image already in the shop library." onClose={onClose} wide>
      <label className="vs-search vs-picker-search"><Search size={17} /><input autoFocus value={search} placeholder="Search all images" onChange={(event) => { setSearch(event.target.value); setPage(1); }} /></label>
      {loading ? <Busy label="Searching images" /> : items.length ? (
        <div className="vs-media-picker">
          {items.map((item) => (
            <button key={item.id} type="button" className={selectedId === item.id ? "is-selected" : ""} onClick={() => onSelect(item)}>
              <MediaThumb media={item} alt={item.filename} />
              <span>{item.filename}</span>
              {selectedId === item.id ? <Check size={17} aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
      ) : <Empty icon={ImageIcon} title="No images found" text="Upload an owner-supplied image or change the search." />}
      <Pager page={page} total={total} pageSize={PICKER_PAGE_SIZE} onPage={setPage} label="images" />
    </Modal>
  );
}

function GalleryPicker({ media, onFindMedia, selectedIds, onChange, onClose }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(media.length);
  const [items, setItems] = useState(media.filter((item) => item.status !== "archived").slice(0, PICKER_PAGE_SIZE));
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      const result = await onFindMedia({ search, page, pageSize: PICKER_PAGE_SIZE, status: "active" });
      if (active) {
        setItems(result.items);
        setTotal(result.total);
        setLoading(false);
      }
    }, 180);
    return () => { active = false; clearTimeout(timer); };
  }, [search, page, onFindMedia]);
  return (
    <Modal title="Choose gallery images" subtitle="Select up to eight owner-supplied images." onClose={onClose} wide>
      <label className="vs-search vs-picker-search"><Search size={17} /><input autoFocus value={search} placeholder="Search all gallery images" onChange={(event) => { setSearch(event.target.value); setPage(1); }} /></label>
      {loading ? <Busy label="Searching images" /> : <div className="vs-media-picker">
        {items.map((item) => {
          const selected = selectedIds.includes(item.id);
          return <button key={item.id} type="button" className={selected ? "is-selected" : ""} onClick={() => {
            if (selected) onChange(selectedIds.filter((id) => id !== item.id));
            else if (selectedIds.length < 8) onChange([...selectedIds, item.id]);
          }}><MediaThumb media={item} alt={item.filename} /><span>{item.filename}</span>{selected ? <Check size={17} /> : null}</button>;
        })}
      </div>}
      {!loading && !items.length ? <Empty icon={ImageIcon} title="No images found" text="Upload an owner-supplied image or change the search." /> : null}
      <Pager page={page} total={total} pageSize={PICKER_PAGE_SIZE} onPage={setPage} label="images" />
      <footer className="vs-modal-actions"><button type="button" className="vs-primary" onClick={onClose}>Done</button></footer>
    </Modal>
  );
}

function ProductEditor({ product, media, onFindMedia, placement, saving, onSave, onClose }) {
  const [form, setForm] = useState(() => blankProduct(product || {}));
  const [errors, setErrors] = useState({});
  const [picker, setPicker] = useState(null);
  const [selectedMainMedia, setSelectedMainMedia] = useState(product?.mainMedia || null);
  const selectedMain = selectedMainMedia || media.find((item) => item.id === form.mainMediaId) || null;
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  function save(publish) {
    const found = validateProduct(form);
    setErrors(found);
    if (Object.keys(found).length) return;
    onSave(productPayload(form, publish));
  }

  function addSpecification() {
    if (form.specifications.length < 30) set("specifications", [...form.specifications, { label: "", value: "" }]);
  }

  function updateBuildSlot(index, values) {
    set("buildDefinition", {
      slots: form.buildDefinition.slots.map((slot, slotIndex) => slotIndex === index ? { ...slot, ...values } : slot),
    });
  }

  function addBuildSlot() {
    if (form.buildDefinition.slots.length < 40) {
      set("buildDefinition", { slots: [...form.buildDefinition.slots, { key: "", label: "", accepts: [], capacity: 1 }] });
    }
  }

  return (
    <Modal
      title={product?.id ? "Edit product" : "Add product"}
      subtitle={placement ? `This product will fill Page ${placement.pageNumber}, Slot ${placement.slotNumber}.` : "Only customer-facing virtual product information is required."}
      onClose={onClose}
      wide
    >
      <div className="vs-editor-grid">
        <div className="vs-product-image-field">
          <button type="button" className="vs-product-image-preview" onClick={() => setPicker("main")}>
            <MediaThumb media={selectedMain} alt={form.name} />
            <span>{selectedMain ? "Change main image" : "Choose main image"}</span>
          </button>
          {errors.mainMediaId ? <small className="vs-field-error">{errors.mainMediaId}</small> : null}
          <button type="button" className="vs-secondary" onClick={() => setPicker("gallery")}>Choose gallery images</button>
          <small>{form.galleryMediaIds.length} of 8 gallery images selected</small>
        </div>

        <div className="vs-form-stack">
          <label>Product name<input value={form.name} maxLength={200} onChange={(event) => set("name", event.target.value)} />{errors.name ? <small className="vs-field-error">{errors.name}</small> : null}</label>
          <label>Short card text<textarea value={form.shortText} maxLength={400} rows={3} onChange={(event) => set("shortText", event.target.value)} />{errors.shortText ? <small className="vs-field-error">{errors.shortText}</small> : null}</label>
          <div className="vs-two-fields">
            <label>Shop Credit cost<input type="number" min="1" step="1" value={form.shopCreditCost} onChange={(event) => set("shopCreditCost", event.target.value)} />{errors.shopCreditCost ? <small className="vs-field-error">{errors.shopCreditCost}</small> : null}</label>
            <label>Display retail value <span>Optional</span><input value={form.displayRetailValue} maxLength={80} placeholder="e.g. $149.00" onChange={(event) => set("displayRetailValue", event.target.value)} /></label>
          </div>
          <section className="vs-experience-fields" aria-label="Product progression and building">
            <div className="vs-field-heading"><span>Virtual ownership experience</span><small>Controls progression, rewards and Build Studio compatibility.</small></div>
            <ChoiceButtons label="Progression level" value={form.progressionLevel} onChange={(value) => set("progressionLevel", value)} items={PROGRESSION_LEVELS} />
            {errors.progressionLevel ? <small className="vs-field-error">{errors.progressionLevel}</small> : null}
            <fieldset className="vs-discovery-flags">
              <legend>Discovery placement</legend>
              <label><input type="checkbox" checked={form.new} onChange={(event) => set("new", event.target.checked)} /><span><b>Fresh arrival</b><small>Prioritise in the New discovery rail.</small></span></label>
              <label><input type="checkbox" checked={form.featured} onChange={(event) => set("featured", event.target.checked)} /><span><b>Featured</b><small>Curate into the shop spotlight.</small></span></label>
              <label><input type="checkbox" checked={form.premium} onChange={(event) => set("premium", event.target.checked)} /><span><b>Premium</b><small>Place in the aspirational collection.</small></span></label>
            </fieldset>
            <ChoiceButtons label="Asset type" value={form.assetKind} onChange={(value) => set("assetKind", value)} items={ASSET_KINDS.map((value) => ({ value, label: ASSET_KIND_LABELS[value] }))} />
            {errors.assetKind ? <small className="vs-field-error">{errors.assetKind}</small> : null}
            <label>Fits these build roles <span>Optional</span><input value={form.installRoles.join(", ")} maxLength={800} placeholder="e.g. vehicle-wheel, sports-car-engine, lounge-furniture" onChange={(event) => set("installRoles", event.target.value.split(",").map((value) => value.trim()).filter(Boolean))} /><small>Comma-separated compatibility keys. Parts appear only in matching customer build slots.</small>{errors.installRoles ? <small className="vs-field-error">{errors.installRoles}</small> : null}</label>
            <div className="vs-build-slot-editor">
              <div className="vs-field-heading"><span>Build Studio slots <small>For vehicles, properties or other containers</small></span><button type="button" disabled={form.buildDefinition.slots.length >= 40} onClick={addBuildSlot}><Plus size={15} /> Add slot</button></div>
              {errors.buildDefinition ? <small className="vs-field-error">{errors.buildDefinition}</small> : null}
              {form.buildDefinition.slots.length ? form.buildDefinition.slots.map((slot, index) => (
                <div className="vs-build-slot-row" key={index}>
                  <label>Slot key<input aria-label={`Build slot ${index + 1} key`} maxLength={64} placeholder="engine" value={slot.key} onChange={(event) => updateBuildSlot(index, { key: event.target.value })} /></label>
                  <label>Customer label<input aria-label={`Build slot ${index + 1} label`} maxLength={100} placeholder="Engine bay" value={slot.label} onChange={(event) => updateBuildSlot(index, { label: event.target.value })} /></label>
                  <label>Accepts roles<input aria-label={`Build slot ${index + 1} accepted roles`} maxLength={800} placeholder="vehicle-engine, sports-car-engine" value={slot.accepts.join(", ")} onChange={(event) => updateBuildSlot(index, { accepts: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) })} /></label>
                  <label>Capacity<input aria-label={`Build slot ${index + 1} capacity`} type="number" min="1" max="1000" step="1" value={slot.capacity} onChange={(event) => updateBuildSlot(index, { capacity: event.target.value })} /></label>
                  <button type="button" className="vs-icon-button" aria-label={`Remove build slot ${index + 1}`} onClick={() => set("buildDefinition", { slots: form.buildDefinition.slots.filter((_, slotIndex) => slotIndex !== index) })}><Trash2 size={16} /></button>
                </div>
              )) : <p className="vs-build-slot-empty">No fitting slots. Add slots such as engine, wheels, kitchen, bedroom or display cabinet when this item can hold other owned items.</p>}
            </div>
          </section>
          <label>Additional description <span>Optional</span><textarea value={form.description} maxLength={6000} rows={5} onChange={(event) => set("description", event.target.value)} /></label>

          <div className="vs-specifications">
            <div className="vs-field-heading"><span>Specifications <small>Optional</small></span><button type="button" disabled={form.specifications.length >= 30} onClick={addSpecification}><Plus size={15} /> Add row</button></div>
            {errors.specifications ? <small className="vs-field-error">{errors.specifications}</small> : null}
            {form.specifications.map((row, index) => (
              <div className="vs-spec-row" key={index}>
                <input aria-label={`Specification ${index + 1} label`} placeholder="Label" maxLength={100} value={row.label} onChange={(event) => set("specifications", form.specifications.map((item, i) => i === index ? { ...item, label: event.target.value } : item))} />
                <input aria-label={`Specification ${index + 1} value`} placeholder="Value" maxLength={500} value={row.value} onChange={(event) => set("specifications", form.specifications.map((item, i) => i === index ? { ...item, value: event.target.value } : item))} />
                <button type="button" className="vs-icon-button" aria-label={`Remove specification ${index + 1}`} onClick={() => set("specifications", form.specifications.filter((_, i) => i !== index))}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer className="vs-modal-actions">
        <button type="button" className="vs-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="vs-secondary" disabled={saving} onClick={() => save(false)}>{saving ? "Saving…" : "Save Draft"}</button>
        <button type="button" className="vs-primary" disabled={saving} onClick={() => save(true)}><Eye size={16} /> Save & Publish</button>
      </footer>

      {picker === "main" ? (
        <MediaPicker media={media} onFindMedia={onFindMedia} selectedId={form.mainMediaId} onClose={() => setPicker(null)} onSelect={(item) => { set("mainMediaId", item.id); setSelectedMainMedia(item); setPicker(null); }} />
      ) : null}
      {picker === "gallery" ? (
        <GalleryPicker media={media} onFindMedia={onFindMedia} selectedIds={form.galleryMediaIds} onChange={(ids) => set("galleryMediaIds", ids)} onClose={() => setPicker(null)} />
      ) : null}
    </Modal>
  );
}

function Dashboard({ data, loading, onNavigate, onCreateSection, onCreateProduct }) {
  if (loading) return <Busy label="Loading dashboard" />;
  const cards = [
    ["Shop Sections", data.totalSections ?? 0, Boxes],
    ["Published Sections", data.publishedSections ?? 0, Eye],
    ["Manual Pages", data.totalPages ?? 0, Settings2],
    ["Filled Slots", data.filledSlots ?? 0, Package],
    ["Empty Slots", data.emptySlots ?? 0, CirclePlus],
    ["Published Products", data.publishedProducts ?? 0, ShoppingBag],
  ];
  return (
    <div className="vs-screen-stack">
      <section className="vs-welcome-panel">
        <div><span className="vs-eyebrow">OWNER CATALOGUE</span><h2>Build the shop one clear page at a time.</h2><p>Create a section, fill its twelve positions, then add the next page only when you choose.</p></div>
        <div className="vs-actions"><button className="vs-primary" onClick={onCreateSection}><Plus size={17} /> Add Shop Section</button><button className="vs-secondary" onClick={onCreateProduct}><Package size={17} /> Add Product</button></div>
      </section>
      <section className="vs-stat-grid">{cards.map(([label, value, Icon]) => <article key={label}><Icon size={20} /><strong>{value}</strong><span>{label}</span></article>)}</section>
      <section className="vs-panel">
        <div className="vs-panel-heading"><div><h3>Quick management</h3><p>Go straight to the catalogue area you need.</p></div></div>
        <div className="vs-quick-grid">
          <button onClick={() => onNavigate("sections")}><Boxes /><span><strong>Shop Sections</strong><small>Pages and exact card positions</small></span><ChevronRight /></button>
          <button onClick={() => onNavigate("products")}><Package /><span><strong>Products</strong><small>Customer-facing product details</small></span><ChevronRight /></button>
          <button onClick={() => onNavigate("media")}><ImageIcon /><span><strong>Media / Images</strong><small>Upload once and reuse safely</small></span><ChevronRight /></button>
        </div>
      </section>
    </div>
  );
}

function CreateSection({ media, onFindMedia, saving, onSave, onClose }) {
  const [form, setForm] = useState({ name: "", heading: "", supportingText: "", heroMediaId: null, status: "draft" });
  const [picker, setPicker] = useState(false);
  const [heroPreview, setHeroPreview] = useState(null);
  const hero = heroPreview || media.find((item) => item.id === form.heroMediaId);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <Modal title="Add Shop Section" subtitle="Page 1 and twelve empty product positions are created automatically." onClose={onClose} wide>
      <div className="vs-form-stack">
        <label>Owner-facing section name<input autoFocus value={form.name} maxLength={160} onChange={(event) => { set("name", event.target.value); if (!form.heading) set("heading", event.target.value); }} /></label>
        <label>Page 1 customer heading<input value={form.heading} maxLength={240} onChange={(event) => set("heading", event.target.value)} /></label>
        <label>Supporting text<textarea rows={4} maxLength={2000} value={form.supportingText} onChange={(event) => set("supportingText", event.target.value)} /></label>
        <div className="vs-inline-image"><MediaThumb media={hero} alt="Section hero" /><div><strong>{hero?.filename || "No hero image selected"}</strong><button type="button" className="vs-secondary" onClick={() => setPicker(true)}>Choose image</button>{hero ? <button type="button" className="vs-link-button" onClick={() => { set("heroMediaId", null); setHeroPreview(null); }}>Remove</button> : null}</div></div>
        <ChoiceButtons label="Status" value={form.status} onChange={(value) => set("status", value)} items={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />
      </div>
      <footer className="vs-modal-actions"><button className="vs-secondary" type="button" onClick={onClose}>Cancel</button><button className="vs-primary" type="button" disabled={saving || !form.name.trim() || !form.heading.trim()} onClick={() => onSave(form)}>{saving ? "Creating…" : "Create Shop Section"}</button></footer>
      {picker ? <MediaPicker media={media} onFindMedia={onFindMedia} selectedId={form.heroMediaId} onClose={() => setPicker(false)} onSelect={(item) => { set("heroMediaId", item.id); setHeroPreview(item); setPicker(false); }} /> : null}
    </Modal>
  );
}

function SectionList({ sections, loading, search, setSearch, status, setStatus, page, total, onPage, onOpen, onCreate }) {
  if (loading) return <Busy label="Loading shop sections" />;
  return (
    <section className="vs-panel">
      <div className="vs-panel-heading"><div><h2>Shop Sections</h2><p>Every manual page contains exactly twelve persistent positions.</p></div><button className="vs-primary" onClick={onCreate}><Plus size={17} /> Add Shop Section</button></div>
      <div className="vs-toolbar"><label className="vs-search"><Search size={17} /><input value={search} placeholder="Search sections" onChange={(event) => setSearch(event.target.value)} /></label><ChoiceButtons className="vs-toolbar-choices" label="Filter section status" value={status} onChange={setStatus} items={[{ value: "", label: "All" }, ...SHOP_STATUSES.map((item) => ({ value: item, label: STATUS_LABEL[item] }))]} /></div>
      {sections.length ? <div className="vs-section-list">{sections.map((section) => {
        const pages = Array.isArray(section.pages) ? section.pages : [];
        return <button type="button" key={section.id} onClick={() => onOpen(section.id)}><span className="vs-section-mark"><Boxes size={21} /></span><span className="vs-section-main"><span><strong>{section.name}</strong><Status value={section.status} /></span><small>{pages.length} manual {pages.length === 1 ? "page" : "pages"} · {section.filledSlots ?? 0} filled · {section.publishedProducts ?? section.customerVisibleProducts ?? 0} customer-visible</small><span className="vs-page-summary">{pages.map((page) => <em key={page.id}>Page {page.pageNumber} — {page.filledSlots ?? pageFilledCount(page)}/12</em>)}</span></span><ChevronRight size={19} /></button>;
      })}</div> : <Empty icon={Boxes} title="No shop sections" text="Create the first section. Page 1 and its twelve empty positions will be ready immediately." action={<button className="vs-primary" onClick={onCreate}><Plus size={17} /> Add Shop Section</button>} />}
      <Pager page={page} total={total} pageSize={LIST_PAGE_SIZE} onPage={onPage} label="sections" />
    </section>
  );
}

function AssignProduct({ products, onFindProducts, onAssign, onClose }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(products.length);
  const [results, setResults] = useState(products.filter((item) => item.status !== "archived").slice(0, PICKER_PAGE_SIZE));
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      const result = await onFindProducts({ search, page, pageSize: PICKER_PAGE_SIZE });
      if (active) {
        setResults(result.items.filter((item) => item.status !== "archived"));
        setTotal(result.total);
        setLoading(false);
      }
    }, 180);
    return () => { active = false; clearTimeout(timer); };
  }, [search, page, onFindProducts]);
  return (
    <Modal title="Assign existing product" subtitle="The product keeps the same stable identity wherever it is placed." onClose={onClose} wide>
      <label className="vs-search vs-picker-search"><Search size={17} /><input autoFocus value={search} placeholder="Search all products" onChange={(event) => { setSearch(event.target.value); setPage(1); }} /></label>
      {loading ? <Busy label="Searching products" /> : <div className="vs-assign-list">{results.map((product) => <button key={product.id} onClick={() => onAssign(product.id)}><span className="vs-small-thumb"><MediaThumb media={product.mainMedia} alt={product.name} /></span><span><strong>{product.name}</strong><small>{product.shopCreditCost} Shop Credits · {STATUS_LABEL[product.status] || product.status}</small></span><Plus size={17} /></button>)}</div>}
      {!loading && !results.length ? <Empty title="No matching products" text="Create a product in this slot instead." /> : null}
      <Pager page={page} total={total} pageSize={PICKER_PAGE_SIZE} onPage={setPage} label="products" />
    </Modal>
  );
}

function MoveSlot({ source, sourcePageId, pages, onMove, onClose }) {
  const [destinationPageId, setDestinationPageId] = useState(sourcePageId);
  const [destination, setDestination] = useState("");
  const destinationPage = pages.find((item) => item.id === destinationPageId);
  const choices = Array.from({ length: 12 }, (_, i) => i + 1).filter((item) => destinationPageId !== sourcePageId || item !== source);
  return (
    <Modal title={`Move or swap Slot ${source}`} subtitle="Choose an explicit page and position. Occupied destinations swap; page numbers and other slots never shift." onClose={onClose}>
      <div className="vs-form-stack"><label>Destination page<select value={destinationPageId} onChange={(event) => { setDestinationPageId(event.target.value); setDestination(""); }}>
        {pages.map((item) => <option key={item.id} value={item.id}>Page {item.pageNumber} — {item.filledSlots ?? pageFilledCount(item)}/12 filled</option>)}</select></label></div>
      <ChoiceButtons className="vs-slot-choices" label={`Destination on Page ${destinationPage?.pageNumber || ""}`} autoFocus value={destination} onChange={setDestination}
        items={choices.map((item) => ({ value: String(item), label: `Slot ${item}` }))} />
      <footer className="vs-modal-actions"><button className="vs-secondary" onClick={onClose}>Cancel</button><button className="vs-primary" disabled={!destination} onClick={() => onMove(destinationPageId, Number(destination))}><ArrowLeftRight size={16} /> Move / Swap</button></footer>
    </Modal>
  );
}
function PageEditor({ section, products, media, busy, onReload, onLoadPage, onFindProducts, onFindMedia, onMutate, onEditProduct, onCreateProduct, onBack }) {
  const pages = Array.isArray(section.pages) ? [...section.pages].sort((a, b) => a.pageNumber - b.pageNumber) : [];
  const [pageId, setPageId] = useState(pages[0]?.id || null);
  const page = pages.find((item) => item.id === pageId) || pages[0];
  const [pageForm, setPageForm] = useState(null);
  const [assignSlot, setAssignSlot] = useState(null);
  const [moveSlot, setMoveSlot] = useState(null);
  const [heroPicker, setHeroPicker] = useState(false);
  const [heroPreview, setHeroPreview] = useState(null);
  const [defaultMediaPicker, setDefaultMediaPicker] = useState(false);
  const [defaultMediaPreview, setDefaultMediaPreview] = useState(section.defaultMedia || null);
  const [editSection, setEditSection] = useState(false);
  const [sectionForm, setSectionForm] = useState({ name: section.name || "", status: section.status || "draft" });
  const [loadingPage, setLoadingPage] = useState(false);
  const addPageAttempt = useRef({ inFlight: false, requestKey: null });
  const resolvedPageId = page?.id || null;
  const serverPageHeading = page?.heading || "";
  const serverPageSupportingText = page?.supportingText || "";
  const serverPageHeroMediaId = page?.heroMedia?.id || null;
  const serverPageHeroUrl = page?.heroMedia?.url || null;
  const serverPageHeroAltText = page?.heroMedia?.altText || "";
  const serverPageHeroFilename = page?.heroMedia?.filename || "";
  const serverPageStatus = page?.status || "draft";
  useEffect(() => {
    if (resolvedPageId && resolvedPageId !== pageId) setPageId(resolvedPageId);
  }, [resolvedPageId, pageId]);
  useEffect(() => {
    if (!resolvedPageId) return;
    setPageForm({
      heading: serverPageHeading,
      supportingText: serverPageSupportingText,
      heroMediaId: serverPageHeroMediaId,
      status: serverPageStatus,
    });
    setHeroPreview(serverPageHeroMediaId ? {
      id: serverPageHeroMediaId,
      url: serverPageHeroUrl,
      altText: serverPageHeroAltText,
      filename: serverPageHeroFilename,
    } : null);
  }, [
    resolvedPageId, serverPageHeading, serverPageSupportingText, serverPageHeroMediaId,
    serverPageHeroUrl, serverPageHeroAltText, serverPageHeroFilename, serverPageStatus,
  ]);
  useEffect(() => { setSectionForm({ name: section.name || "", status: section.status || "draft" }); }, [section.name, section.status]);
  useEffect(() => { setDefaultMediaPreview(section.defaultMedia || null); }, [section.defaultMedia]);
  useEffect(() => {
    if (!page?.id || (Array.isArray(page.slots) && page.slots.length === 12)) return undefined;
    let active = true;
    setLoadingPage(true);
    Promise.resolve(onLoadPage(page.id)).finally(() => { if (active) setLoadingPage(false); });
    return () => { active = false; };
  }, [page?.id, page?.slots, onLoadPage]);
  if (!page || !pageForm) return <Empty title="Page 1 is unavailable" text="Refresh the section. The server enforces Page 1 and its twelve positions." action={<button className="vs-secondary" onClick={onReload}>Refresh</button>} />;
  if (loadingPage || !Array.isArray(page.slots) || page.slots.length !== 12) return <Busy label={`Loading Page ${page.pageNumber} positions`} />;
  const slots = normaliseSlots(page.slots);
  const filled = slots.filter((slot) => slot.product).length;

  async function mutate(operation, args, payload, success) {
    const ok = await onMutate(operation, args, payload, success);
    if (ok) { setAssignSlot(null); setMoveSlot(null); }
    return ok;
  }

  async function addNextPage() {
    if (addPageAttempt.current.inFlight) return;
    addPageAttempt.current.inFlight = true;
    addPageAttempt.current.requestKey ||= requestKey();
    try {
      const ok = await mutate(
        MUTATION.ADD_PAGE,
      { sectionId: section.id },
      { requestKey: addPageAttempt.current.requestKey },
      `Page ${nextPageNumber(pages)} created with twelve empty slots.`,
    );
      if (ok) addPageAttempt.current.requestKey = null;
    } finally {
      addPageAttempt.current.inFlight = false;
    }
  }

  return (
    <div className="vs-screen-stack">
      <div className="vs-breadcrumb"><button onClick={onBack}>Shop Sections</button><ChevronRight size={14} /><span>{section.name}</span><ChevronRight size={14} /><strong>Page {page.pageNumber}</strong></div>
      <section className="vs-panel vs-page-editor-header">
        <div className="vs-panel-heading"><div><span className="vs-eyebrow">{section.name}</span><h2>Page {page.pageNumber} <Status value={page.status} /></h2><p><strong>{filled} of 12 filled</strong> · Page numbers remain fixed and owner-controlled.</p></div><div className="vs-actions"><button className="vs-secondary" onClick={() => setEditSection(true)}><Pencil size={16} /> Edit Section</button><button className="vs-secondary" onClick={onReload}><RefreshCw size={16} /> Refresh</button>{page.pageNumber > 1 ? <button className="vs-secondary is-danger" disabled={busy || filled > 0 || page.status === "published"} onClick={() => { if (window.confirm(`Remove empty Page ${page.pageNumber}? Page numbers will not be shifted.`)) mutate(MUTATION.REMOVE_PAGE, { pageId: page.id }, {}, `Page ${page.pageNumber} removed.`); }}><Trash2 size={16} /> Remove Page {page.pageNumber}</button> : null}<button className="vs-primary" disabled={busy} onClick={addNextPage}><Plus size={17} /> Add Page {nextPageNumber(pages)}</button></div></div>
        <nav className="vs-page-tabs" aria-label="Section pages">{pages.map((item) => <button className={item.id === page.id ? "is-active" : ""} key={item.id} onClick={() => setPageId(item.id)}>Page {item.pageNumber}<small>{item.filledSlots ?? pageFilledCount(item)}/12</small></button>)}</nav>
      </section>

      <section className="vs-panel vs-default-media-panel">
        <div className="vs-panel-heading"><div><h3>Default product image</h3><p>Choose one owner-approved image, then apply it only to products in this section that do not already have a main image.</p></div></div>
        <div className="vs-default-media-row">
          <div className="vs-inline-image"><MediaThumb media={defaultMediaPreview} alt="Section default" /></div>
          <div><strong>{defaultMediaPreview?.filename || "No default image selected"}</strong><small>Existing product images are never replaced.</small></div>
          <button className="vs-secondary" onClick={() => setDefaultMediaPicker(true)}><ImageIcon size={16} /> Choose image</button>
          <button className="vs-secondary" disabled={busy || !defaultMediaPreview?.id} onClick={() => mutate(MUTATION.SET_SECTION_DEFAULT_MEDIA, { sectionId: section.id }, { defaultMediaId: defaultMediaPreview.id, applyToProductsWithoutMedia: false }, "Section default image saved.")}>Save default</button>
          <button className="vs-primary" disabled={busy || !defaultMediaPreview?.id} onClick={() => { if (window.confirm("Apply this image to every product in this section that currently has no main image? Existing product images will remain unchanged.")) mutate(MUTATION.SET_SECTION_DEFAULT_MEDIA, { sectionId: section.id }, { defaultMediaId: defaultMediaPreview.id, applyToProductsWithoutMedia: true }, "Default image applied to products without media."); }}><Check size={16} /> Apply to missing images</button>
        </div>
      </section>

      <section className="vs-panel">
        <div className="vs-panel-heading"><div><h3>Page content</h3><p>Every catalogue page uses the same production layout.</p></div><button className="vs-primary" disabled={busy || !pageForm.heading.trim()} onClick={() => mutate(MUTATION.UPDATE_PAGE, { pageId: page.id }, pageForm, "Page content saved.")}>Save Page</button></div>
        <div className="vs-page-form">
          <div className="vs-form-stack"><label>Heading<input value={pageForm.heading} onChange={(event) => setPageForm({ ...pageForm, heading: event.target.value })} /></label><label>Supporting text<textarea rows={3} value={pageForm.supportingText} onChange={(event) => setPageForm({ ...pageForm, supportingText: event.target.value })} /></label><ChoiceButtons label="Page status" value={pageForm.status} onChange={(status) => setPageForm({ ...pageForm, status })} items={SHOP_STATUSES.map((item) => ({ value: item, label: STATUS_LABEL[item] }))} /></div>
          <div className="vs-hero-field"><button type="button" onClick={() => setHeroPicker(true)}><MediaThumb media={heroPreview || media.find((item) => item.id === pageForm.heroMediaId)} alt="Page hero" /><span>{pageForm.heroMediaId ? "Change optional hero" : "Choose optional hero"}</span></button>{pageForm.heroMediaId ? <button type="button" className="vs-link-button is-danger" onClick={() => { setPageForm({ ...pageForm, heroMediaId: null }); setHeroPreview(null); }}>Remove hero</button> : null}</div>
        </div>
      </section>

      <section className="vs-panel">
        <div className="vs-panel-heading"><div><h3>Product positions</h3><p>All twelve positions are persistent. Empty positions will not render to customers.</p></div><strong className="vs-fill-count">{filled} / 12 filled</strong></div>
        <div className="vs-slot-grid">
          {slots.map((slot) => slot.product ? (
            <article className="vs-slot is-filled" key={slot.slotNumber}>
              <span className="vs-slot-number">{slot.slotNumber}</span><Status value={slot.product.status} />
              <div className="vs-slot-image"><MediaThumb media={slot.product.mainMedia} alt={slot.product.name} /></div>
              <div className="vs-slot-copy"><strong>{slot.product.name}</strong><span>{slot.product.shopCreditCost} Shop Credits</span></div>
              <div className="vs-slot-actions"><button onClick={() => onEditProduct(slot.product, { pageId: page.id, pageNumber: page.pageNumber, slotId: slot.id, slotNumber: slot.slotNumber })}><Pencil size={15} /> Edit</button><button onClick={() => setAssignSlot(slot)}><RefreshCw size={15} /> Replace</button><button onClick={() => setMoveSlot(slot.slotNumber)}><ArrowLeftRight size={15} /> Move</button><button onClick={() => mutate(MUTATION.PRODUCT_STATUS, { productId: slot.product.id }, { status: slot.product.status === "published" ? "hidden" : "published" }, "Product status updated.")}>{slot.product.status === "published" ? <EyeOff size={15} /> : <Eye size={15} />}{slot.product.status === "published" ? "Hide" : "Publish"}</button><button className="is-danger" onClick={() => mutate(MUTATION.CLEAR_SLOT, { slotId: slot.id }, {}, `Slot ${slot.slotNumber} cleared. The product was not deleted.`)}><X size={15} /> Clear</button></div>
            </article>
          ) : (
            <article className="vs-slot is-empty" key={slot.slotNumber}>
              <span className="vs-slot-number">{slot.slotNumber}</span><span className="vs-empty-label">Empty</span><CirclePlus size={29} aria-hidden="true" /><strong>Product position {slot.slotNumber}</strong><div><button className="vs-primary" onClick={() => onCreateProduct({ pageId: page.id, pageNumber: page.pageNumber, slotId: slot.id, slotNumber: slot.slotNumber })}>Create Product Here</button><button className="vs-secondary" onClick={() => setAssignSlot(slot)}>Assign Existing</button></div>
            </article>
          ))}
        </div>
      </section>
      {assignSlot ? <AssignProduct products={products} onFindProducts={onFindProducts} onClose={() => setAssignSlot(null)} onAssign={(productId) => mutate(MUTATION.ASSIGN_SLOT, { slotId: assignSlot.id }, { productId }, `Slot ${assignSlot.slotNumber} updated.`)} /> : null}
      {moveSlot ? <MoveSlot source={moveSlot} sourcePageId={page.id} pages={pages} onClose={() => setMoveSlot(null)} onMove={(destinationPageId, destinationSlot) => mutate(MUTATION.MOVE_SLOT, { pageId: page.id }, { sourceSlotNumber: moveSlot, destinationPageId, destinationSlotNumber: destinationSlot }, "Product position persisted on the selected page and slot.")} /> : null}
      {heroPicker ? <MediaPicker media={media} onFindMedia={onFindMedia} selectedId={pageForm.heroMediaId} onClose={() => setHeroPicker(false)} onSelect={(item) => { setPageForm({ ...pageForm, heroMediaId: item.id }); setHeroPreview(item); setHeroPicker(false); }} /> : null}
      {defaultMediaPicker ? <MediaPicker media={media} onFindMedia={onFindMedia} selectedId={defaultMediaPreview?.id} onClose={() => setDefaultMediaPicker(false)} onSelect={(item) => { setDefaultMediaPreview(item); setDefaultMediaPicker(false); }} /> : null}
      {editSection ? (
        <Modal title="Edit Shop Section" subtitle="Update the owner label and customer publication state." onClose={() => setEditSection(false)}>
          <div className="vs-form-stack">
            <label>Section name<input value={sectionForm.name} maxLength={180} onChange={(event) => setSectionForm({ ...sectionForm, name: event.target.value })} /></label>
            <ChoiceButtons label="Section status" value={sectionForm.status} onChange={(status) => setSectionForm({ ...sectionForm, status })} items={SHOP_STATUSES.map((item) => ({ value: item, label: STATUS_LABEL[item] }))} />
          </div>
          <footer className="vs-modal-actions"><button className="vs-secondary" onClick={() => setEditSection(false)}>Cancel</button><button className="vs-primary" disabled={busy || !sectionForm.name.trim()} onClick={async () => { if (await mutate(MUTATION.UPDATE_SECTION, { sectionId: section.id }, sectionForm, "Shop Section updated.")) setEditSection(false); }}>Save Section</button></footer>
        </Modal>
      ) : null}
    </div>
  );
}

function Products({ products, loading, search, setSearch, status, setStatus, placement, setPlacement, page, total, onPage, onEdit, onCreate, onMutate }) {
  if (loading) return <Busy label="Loading products" />;
  return (
    <section className="vs-panel">
      <div className="vs-panel-heading"><div><h2>Products</h2><p>Stable virtual products, independent from where they are placed.</p></div><button className="vs-primary" onClick={() => onCreate(null)}><Plus size={17} /> Add Product</button></div>
      <div className="vs-toolbar"><label className="vs-search"><Search size={17} /><input value={search} placeholder="Search products" onChange={(event) => setSearch(event.target.value)} /></label><ChoiceButtons className="vs-toolbar-choices" label="Filter product status" value={status} onChange={setStatus} items={[{ value: "", label: "All" }, ...SHOP_STATUSES.map((item) => ({ value: item, label: STATUS_LABEL[item] }))]} /><ChoiceButtons className="vs-toolbar-choices" label="Filter product placement" value={placement} onChange={setPlacement} items={[{ value: "", label: "All" }, { value: "placed", label: "Placed" }, { value: "unplaced", label: "Unplaced" }]} /></div>
      {products.length ? <div className="vs-product-table"><div className="vs-table-head"><span>Product</span><span>Shop Credits</span><span>Status</span><span>Placement</span><span>Updated</span><span>Actions</span></div>{products.map((product) => <article key={product.id}><span className="vs-product-cell"><span className="vs-small-thumb"><MediaThumb media={product.mainMedia} alt={product.name} /></span><span><strong>{product.name}</strong><small>{product.shortText}</small></span></span><strong>{product.shopCreditCost}</strong><Status value={product.status} /><span>{product.placements?.length ? product.placements.map((item) => `${item.sectionName}, P${item.pageNumber}/S${item.slotNumber}`).join(" · ") : "Unplaced"}</span><time>{product.updatedAt ? new Date(product.updatedAt).toLocaleDateString() : "—"}</time><span className="vs-row-actions"><button onClick={() => onEdit(product)} title="Edit"><Pencil size={16} /></button><button onClick={() => onMutate(MUTATION.DUPLICATE_PRODUCT, { productId: product.id }, {}, "Draft duplicate created.")} title="Duplicate"><Boxes size={16} /></button><button onClick={() => onMutate(MUTATION.PRODUCT_STATUS, { productId: product.id }, { status: product.status === "published" ? "hidden" : "published" }, "Product status updated.")} title={product.status === "published" ? "Hide" : "Publish"}>{product.status === "published" ? <EyeOff size={16} /> : <Eye size={16} />}</button><button className="is-danger" onClick={() => onMutate(MUTATION.PRODUCT_STATUS, { productId: product.id }, { status: "archived" }, "Product archived.")} title="Archive"><Archive size={16} /></button></span></article>)}</div> : <Empty title="No products" text="Add a product directly, or create one from an empty page slot." action={<button className="vs-primary" onClick={() => onCreate(null)}><Plus size={17} /> Add Product</button>} />}
      <Pager page={page} total={total} pageSize={LIST_PAGE_SIZE} onPage={onPage} label="products" />
    </section>
  );
}

function Media({ media, loading, api, onReload, onMutate, setNotice, search, setSearch, status, setStatus, page, total, onPage }) {
  const [altText, setAltText] = useState("");
  const [uploading, setUploading] = useState(false);

  async function upload(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!MEDIA_TYPES.includes(file.type) || file.size <= 0 || file.size > MAX_MEDIA_BYTES) {
      setNotice({ kind: "error", message: "Choose a JPEG, PNG, or WebP image no larger than 8 MiB." });
      return;
    }
    setUploading(true);
    const result = await api.uploadMedia(file, altText || file.name.replace(/[.][^.]+$/, ""));
    setUploading(false);
    if (!result.ok) { setNotice({ kind: "error", message: errorMessage(result, "The image could not be uploaded.") }); return; }
    setAltText("");
    setNotice({ kind: "success", message: "Image uploaded and ready to use." });
    onReload();
  }

  return (
    <div className="vs-screen-stack">
      <section className="vs-panel">
        <div className="vs-panel-heading"><div><h2>Media / Images</h2><p>Owner-supplied JPEG, PNG, and WebP images. Upload once and reuse.</p></div></div>
        <div className="vs-upload-row"><label>Image description / alt text<input value={altText} maxLength={240} placeholder="Describe the image" onChange={(event) => setAltText(event.target.value)} /></label><label className={"vs-upload-button" + (uploading ? " is-disabled" : "")}><Upload size={17} />{uploading ? "Uploading…" : "Upload Image"}<input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={upload} /></label></div>
      </section>
      <section className="vs-panel">
        <div className="vs-toolbar"><label className="vs-search"><Search size={17} /><input value={search} placeholder="Search all images" onChange={(event) => { setSearch(event.target.value); onPage(1); }} /></label><ChoiceButtons className="vs-toolbar-choices" label="Filter media status" value={status} onChange={(value) => { setStatus(value); onPage(1); }} items={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived" }, { value: "", label: "All" }]} /></div>
        {loading ? <Busy label="Loading media" /> : media.length ? <div className="vs-media-library">{media.map((item) => <article key={item.id}><div><MediaThumb media={item} alt={item.filename} /></div><strong>{item.filename}</strong><small>{Math.ceil((item.sizeBytes || 0) / 1024)} KB · {item.contentType}</small><small>{item.usage?.total || 0} references</small><Status value={item.status === "active" ? "published" : "archived"} />{item.status !== "archived" ? <button disabled={(item.usage?.total || 0) > 0} onClick={() => onMutate(MUTATION.ARCHIVE_MEDIA, { mediaId: item.id }, {}, "Image archived.")}><Archive size={15} /> Archive</button> : null}</article>)}</div> : <Empty icon={ImageIcon} title="No images found" text="Upload an owner-supplied image or change the search and status filters." />}
        <Pager page={page} total={total} pageSize={PICKER_PAGE_SIZE} onPage={onPage} label="images" />
      </section>
    </div>
  );
}
function OwnerWorkspace({ api }) {
  const [gate, setGate] = useState({ state: "loading", owner: null });
  const [screen, setScreen] = useState("dashboard");
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState({});
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [media, setMedia] = useState([]);
  const [sectionId, setSectionId] = useState(null);
  const [sectionDetail, setSectionDetail] = useState(null);
  const [createSection, setCreateSection] = useState(false);
  const [productEditor, setProductEditor] = useState(null);
  const [sectionSearch, setSectionSearch] = useState("");
  const [sectionStatus, setSectionStatus] = useState("");
  const [sectionPage, setSectionPage] = useState(1);
  const [sectionTotal, setSectionTotal] = useState(0);
  const [productSearch, setProductSearch] = useState("");
  const [productStatus, setProductStatus] = useState("");
  const [productPlacement, setProductPlacement] = useState("");
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [mediaSearch, setMediaSearch] = useState("");
  const [mediaStatus, setMediaStatus] = useState("active");
  const [mediaPage, setMediaPage] = useState(1);
  const [mediaTotal, setMediaTotal] = useState(0);
  const mutationInFlight = useRef(false);
  const sectionAttempt = useRef({ inFlight: false, requestKey: null });
  const productSaveInFlight = useRef(false);

  const session = useCallback(async () => {
    setGate({ state: "loading", owner: null });
    const result = await api.read(READ.SESSION);
    if (result.ok) {
      const data = result.data || {};
      const authorized = data.authorized === true || data.ok === true;
      setGate(authorized ? { state: "authorized", owner: data.owner || data.account || {} } : { state: "forbidden", owner: null });
    } else setGate({ state: result.state || "unavailable", owner: null });
  }, [api]);

  useEffect(() => { session(); }, [session]);

  const loadDashboard = useCallback(async () => {
    setLoading(true); const result = await api.read(READ.DASHBOARD); setLoading(false);
    if (result.ok) setDashboard(responseValue(result, ["dashboard", "counts"], result.data || {}));
    else setNotice({ kind: "error", message: errorMessage(result, "Dashboard data is unavailable.") });
  }, [api]);
  const loadSections = useCallback(async () => {
    setLoading(true);
    const result = await api.read(READ.SECTIONS, {
      page: sectionPage,
      pageSize: LIST_PAGE_SIZE,
      search: sectionSearch,
      status: sectionStatus,
    });
    setLoading(false);
    if (result.ok) {
      setSections(responseValue(result, ["items", "sections"], []));
      setSectionTotal(Number(responseValue(result, ["total"], 0)) || 0);
    } else {
      setNotice({ kind: "error", message: errorMessage(result, "Shop Sections are unavailable.") });
    }
  }, [api, sectionPage, sectionSearch, sectionStatus]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    const result = await api.read(READ.PRODUCTS, {
      page: productPage,
      pageSize: LIST_PAGE_SIZE,
      search: productSearch,
      status: productStatus,
      placement: productPlacement,
    });
    setLoading(false);
    if (result.ok) {
      setProducts(responseValue(result, ["items", "products"], []));
      setProductTotal(Number(responseValue(result, ["total"], 0)) || 0);
    } else {
      setNotice({ kind: "error", message: errorMessage(result, "Products are unavailable.") });
    }
  }, [api, productPage, productSearch, productStatus, productPlacement]);

  const findProducts = useCallback(async ({ search = "", page = 1, pageSize = PICKER_PAGE_SIZE, status = "" } = {}) => {
    const result = await api.read(READ.PRODUCTS, { page, pageSize, search, status, placement: "" });
    return {
      ok: result.ok,
      items: result.ok ? responseValue(result, ["items", "products"], []) : [],
      total: result.ok ? Number(responseValue(result, ["total"], 0)) || 0 : 0,
      page: result.ok ? Number(responseValue(result, ["page"], page)) || page : page,
    };
  }, [api]);

  const hydrateMedia = useCallback(async (items) => Promise.all(items.map(async (item) => {
    if (item.url) return item;
    const result = await api.mediaContent(item.id);
    const contentType = result.data?.contentType;
    const base64 = result.data?.base64;
    if (!result.ok || !MEDIA_TYPES.includes(contentType) || typeof base64 !== "string") return item;
    return { ...item, url: "data:" + contentType + ";base64," + base64 };
  })), [api]);

  const findMedia = useCallback(async ({ search = "", page = 1, pageSize = PICKER_PAGE_SIZE, status = "active" } = {}) => {
    const result = await api.read(READ.MEDIA, { page, pageSize, search, status });
    const items = result.ok ? responseValue(result, ["items", "media"], []) : [];
    return {
      ok: result.ok,
      items: result.ok ? await hydrateMedia(items) : [],
      total: result.ok ? Number(responseValue(result, ["total"], 0)) || 0 : 0,
      page: result.ok ? Number(responseValue(result, ["page"], page)) || page : page,
    };
  }, [api, hydrateMedia]);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    const result = await findMedia({
      page: mediaPage,
      pageSize: PICKER_PAGE_SIZE,
      search: mediaSearch,
      status: mediaStatus,
    });
    setLoading(false);
    if (result.ok) {
      setMedia(result.items);
      setMediaTotal(result.total);
    } else {
      setNotice({ kind: "error", message: "Media images are unavailable." });
    }
  }, [findMedia, mediaPage, mediaSearch, mediaStatus]);
  const loadSection = useCallback(async (id = sectionId) => {
    if (!id) return;
    setLoading(true);
    const result = await api.read(READ.SECTION, { sectionId: id });
    if (result.ok) setSectionDetail(responseValue(result, ["section"], result.data));
    else setNotice({ kind: "error", message: errorMessage(result, "The section is unavailable.") });
    setLoading(false);
  }, [api, sectionId]);
  const loadSectionPage = useCallback(async (pageId) => {
    const result = await api.read(READ.PAGE, { pageId });
    if (!result.ok) {
      setNotice({ kind: "error", message: errorMessage(result, "The page positions are unavailable.") });
      return false;
    }
    const detail = responseValue(result, ["page"], null);
    if (!detail) return false;
    setSectionDetail((current) => current ? {
      ...current,
      pages: (current.pages || []).map((page) => page.id === detail.id ? detail : page),
    } : current);
    return true;
  }, [api]);

  useEffect(() => {
    if (gate.state !== "authorized") return;
    if (screen === "dashboard") loadDashboard();
    if (screen === "sections" && !sectionId) loadSections();
    if (screen === "products") loadProducts();
    if (screen === "media") loadMedia();
  }, [gate.state, screen, sectionId, loadDashboard, loadSections, loadProducts, loadMedia]);
  useEffect(() => {
    if (gate.state === "authorized" && sectionId) loadSection(sectionId);
  }, [gate.state, sectionId, loadSection]);

  async function mutate(operation, args = {}, payload = {}, success = "Saved.") {
    if (mutationInFlight.current) return false;
    mutationInFlight.current = true;
    setBusy(true);
    try {
      const result = await api.mutate(operation, args, payload);
      if (!result.ok) { setNotice({ kind: "error", message: errorMessage(result) }); if (["expired", "signed-out", "forbidden"].includes(result.state)) setGate({ state: result.state, owner: null }); return false; }
      setNotice({ kind: "success", message: success });
      await Promise.all([loadDashboard(), loadProducts(), loadMedia(), sectionId ? loadSection(sectionId) : loadSections()]);
      return true;
    } finally {
      mutationInFlight.current = false;
      setBusy(false);
    }
  }

  async function saveSection(payload) {
    if (sectionAttempt.current.inFlight) return;
    sectionAttempt.current.inFlight = true;
    sectionAttempt.current.requestKey ||= requestKey();
    setBusy(true);
    try {
      const result = await api.mutate(MUTATION.CREATE_SECTION, {}, {
        ...payload,
        requestKey: sectionAttempt.current.requestKey,
      });
      if (!result.ok) { setNotice({ kind: "error", message: errorMessage(result) }); return; }
      sectionAttempt.current.requestKey = null;
      const section = responseValue(result, ["section"], null);
      setCreateSection(false);
      setNotice({ kind: "success", message: "Shop Section created. Page 1 has twelve empty positions." });
      setScreen("sections");
      if (section?.id) setSectionId(section.id); else loadSections();
    } finally {
      sectionAttempt.current.inFlight = false;
      setBusy(false);
    }
  }

  async function saveProduct(payload) {
    if (productSaveInFlight.current) return;
    productSaveInFlight.current = true;
    const current = productEditor || {};
    const isEdit = !!current.product?.id;
    const args = isEdit
      ? { productId: current.product.id }
      : (current.placement ? { slotId: current.placement.slotId } : {});
    setBusy(true);
    try {
      const result = await api.mutate(isEdit ? MUTATION.UPDATE_PRODUCT : MUTATION.CREATE_PRODUCT, args, payload);
      if (!result.ok) { setNotice({ kind: "error", message: errorMessage(result, "The product could not be saved.") }); return; }
      setProductEditor(null);
      setNotice({ kind: "success", message: current.placement ? `Product saved in Slot ${current.placement.slotNumber}.` : "Product saved." });
      await Promise.all([loadProducts(), loadDashboard(), sectionId ? loadSection(sectionId) : Promise.resolve()]);
    } finally {
      productSaveInFlight.current = false;
      setBusy(false);
    }
  }

  const currentTitle = sectionDetail ? `${sectionDetail.name} — Page Management` : NAV.find((item) => item.id === screen)?.label || "Virtual Shop";
  if (gate.state === "loading") return <section className="vs-gate"><span className="vs-brand-mark"><ShoppingBag size={28} /></span><Busy label="Confirming VISINVOKE OWNER access" /></section>;
  if (gate.state !== "authorized") return (
    <section className="vs-gate">
      <span className="vs-brand-mark"><ShoppingBag size={28} /></span>
      <span className="vs-eyebrow">VISINVOKE VIRTUAL SHOP</span>
      <h1>{gate.state === "forbidden" ? "Owner access required" : gate.state === "signed-out" || gate.state === "expired" ? "Admin sign-in required" : "Shop service unavailable"}</h1>
      <p>{gate.state === "forbidden" ? "This catalogue workspace is protected by live server-side VISINVOKE OWNER and MFA authorization." : gate.state === "signed-out" || gate.state === "expired" ? "Sign in through the existing VISINVOKE Admin login and complete MFA." : "The secure shop service could not be reached. Your catalogue has not been changed."}</p>
      <div className="vs-actions"><button className="vs-secondary" onClick={session}><RefreshCw size={17} /> Try Again</button></div>
    </section>
  );

  return (
    <div className="vs-app">
      <section className="vs-workspace-header" aria-label="Virtual Shop management">
        <div className="vs-workspace-heading"><span className="vs-eyebrow">OWNER WORKSPACE</span><h2>{currentTitle}</h2></div>
        <nav className="vs-workspace-nav" aria-label="Virtual Shop tools">{NAV.map(({ id, label, icon: Icon }) => <button type="button" className={screen === id && !sectionDetail ? "is-active" : ""} key={id} onClick={() => { setSectionId(null); setSectionDetail(null); setScreen(id); }}><Icon size={17} /><span>{label}</span></button>)}</nav>
      </section>
      <div className="vs-content">
        <Notice notice={notice} onClear={() => setNotice(null)} />
        {screen === "dashboard" ? <Dashboard data={dashboard} loading={loading} onNavigate={setScreen} onCreateSection={() => setCreateSection(true)} onCreateProduct={() => setProductEditor({ product: null, placement: null })} /> : null}
        {screen === "sections" && !sectionDetail ? <SectionList sections={sections} loading={loading} search={sectionSearch} setSearch={(value) => { setSectionSearch(value); setSectionPage(1); }} status={sectionStatus} setStatus={(value) => { setSectionStatus(value); setSectionPage(1); }} page={sectionPage} total={sectionTotal} onPage={setSectionPage} onOpen={(id) => setSectionId(id)} onCreate={() => setCreateSection(true)} /> : null}
        {screen === "sections" && sectionDetail ? <PageEditor section={sectionDetail} products={products} media={media} busy={busy} onReload={() => loadSection(sectionId)} onLoadPage={loadSectionPage} onFindProducts={findProducts} onFindMedia={findMedia} onMutate={mutate} onEditProduct={(product, placement) => setProductEditor({ product, placement })} onCreateProduct={(placement) => setProductEditor({ product: null, placement })} onBack={() => { setSectionId(null); setSectionDetail(null); loadSections(); }} /> : null}
        {screen === "products" ? <Products products={products} loading={loading} search={productSearch} setSearch={(value) => { setProductSearch(value); setProductPage(1); }} status={productStatus} setStatus={(value) => { setProductStatus(value); setProductPage(1); }} placement={productPlacement} setPlacement={(value) => { setProductPlacement(value); setProductPage(1); }} page={productPage} total={productTotal} onPage={setProductPage} onEdit={(product) => setProductEditor({ product, placement: null })} onCreate={(placement) => setProductEditor({ product: null, placement })} onMutate={mutate} /> : null}
        {screen === "configurations" ? <VirtualShopConfigurations api={api} /> : null}
        {screen === "media" ? <Media media={media} loading={loading} api={api} onReload={loadMedia} onMutate={mutate} setNotice={setNotice} search={mediaSearch} setSearch={setMediaSearch} status={mediaStatus} setStatus={setMediaStatus} page={mediaPage} total={mediaTotal} onPage={setMediaPage} /> : null}
        {screen === "import" ? <VirtualShopImporter api={api} onOpenSections={() => { setSectionId(null); setSectionDetail(null); setScreen("sections"); }} onOpenProducts={() => setScreen("products")} onOpenStorefront={() => setScreen("storefront")} /> : null}
        {screen === "ownership" ? <VirtualShopOwnershipSupport api={api} /> : null}
        {screen === "storefront" ? <VirtualShopStorefrontContent api={api} findMedia={findMedia} findProducts={findProducts} mediaPicker={MediaPicker} onOpenProducts={() => setScreen("products")} onOpenSection={(id) => { setSectionDetail(null); setSectionId(id); setScreen("sections"); }} /> : null}
        {screen === "experience" ? <VirtualShopExperienceAdmin api={api} /> : null}
        {screen === "moderation" ? <VirtualShopModeration api={api} /> : null}
      </div>
      {createSection ? <CreateSection media={media} onFindMedia={findMedia} saving={busy} onSave={saveSection} onClose={() => setCreateSection(false)} /> : null}
      {productEditor ? <ProductEditor product={productEditor.product} media={media} onFindMedia={findMedia} placement={productEditor.placement} saving={busy} onSave={saveProduct} onClose={() => setProductEditor(null)} /> : null}
    </div>
  );
}

export default function VirtualShopOwnerAdmin({ api }) {
  return <OwnerWorkspace api={api} />;
}
